<script setup lang="ts">
import type { NuxtError } from '#app'
import paperJetUrl from '~/assets/images/paper-jet.png?url'
import AppLogo from '~/components/layout/AppLogo.vue'
import { Button } from '@/components/ui/button'

const props = defineProps<{
    error: NuxtError
}>()

const { t } = useI18n()

const status = computed(() => props.error.status ?? props.error.statusCode ?? 500)

const errorContent = computed(() => {
    switch (status.value) {
        case 404:
            return {
                title: t('Page not found'),
                description: t("The page you're looking for doesn't exist or has been moved."),
                primaryLabel: t('Go to dashboard'),
                primaryRedirect: '/orders/incoming',
            }
        case 403:
            return {
                title: t('Access denied'),
                description: t("You don't have permission to view this page."),
                primaryLabel: t('Go to dashboard'),
                primaryRedirect: '/orders/incoming',
            }
        case 401:
            return {
                title: t('Session expired'),
                description: t('Please sign in again to continue.'),
                primaryLabel: t('Sign in'),
                primaryRedirect: '/auth/login',
            }
        default:
            return {
                title: t('Something went wrong'),
                description: t('An unexpected error occurred. Please try again or return to the dashboard.'),
                primaryLabel: t('Go to dashboard'),
                primaryRedirect: '/orders/incoming',
            }
    }
})

function handlePrimary() {
    clearError({ redirect: errorContent.value.primaryRedirect })
}

function handleTryAgain() {
    clearError()
}
</script>

<template>
    <NuxtLayout name="auth">
        <template #visual>
            <div
                class="relative flex min-h-[220px] flex-col overflow-hidden bg-brand-primary p-6 text-white sm:min-h-[280px] sm:p-8 lg:min-h-svh lg:p-10"
            >
                <div
                    class="pointer-events-none absolute -left-24 top-1/4 size-56 rounded-full bg-brand-primary-light/20 blur-3xl sm:size-80"
                    aria-hidden="true"
                />
                <div
                    class="pointer-events-none absolute -right-16 bottom-0 size-64 rounded-full bg-brand-yellow/20 blur-3xl sm:size-96"
                    aria-hidden="true"
                />
                <div
                    class="pointer-events-none absolute inset-0 bg-brand-primary/90"
                    aria-hidden="true"
                />

                <div class="relative z-10">
                    <AppLogo class="brightness-0 invert" />
                </div>

                <div class="relative z-10 mt-auto flex max-w-md flex-col gap-6 sm:gap-8">
                    <div class="space-y-2 text-center sm:space-y-4 lg:text-left">
                        <p class="text-caption text-brand-primary-light sm:text-body">
                            {{ status }}
                        </p>
                        <h2 class="text-heading text-white sm:text-2xl lg:text-display">
                            {{ errorContent.title }}
                        </h2>
                    </div>
                </div>
            </div>
        </template>

        <div class="space-y-8">
            <div class="space-y-2 text-center lg:text-left">
                <p class="text-display text-brand-primary">
                    {{ status }}
                </p>
                <h1 class="text-title text-foreground">
                    {{ errorContent.title }}
                </h1>
                <p class="text-body text-muted-foreground">
                    {{ errorContent.description }}
                </p>
            </div>

            <div class="flex flex-col gap-3 sm:flex-row">
                <Button
                    type="button"
                    size="lg"
                    class="w-full sm:w-auto"
                    @click="handlePrimary"
                >
                    {{ errorContent.primaryLabel }}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    class="w-full sm:w-auto"
                    @click="handleTryAgain"
                >
                    {{ $t('Try again') }}
                </Button>
            </div>
        </div>
    </NuxtLayout>
</template>
